import axios from 'axios';
import { idVisitante } from '../utils/visitante';

// Configuración base de axios
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Host del backend para construir URLs de assets (imágenes, archivos estáticos)
export const API_HOST = process.env.REACT_APP_API_HOST || API_URL.replace(/\/api$/, '');

// Prefija con API_HOST si la url es relativa (/static/...). Si ya es absoluta
// (http://, https:// o data:) la devuelve tal cual. Sin esto, el browser
// resuelve URLs relativas contra el origen del frontend y devuelve el SPA
// fallback (200 text/html) en vez de la imagen. Helper compartido por todas
// las páginas del micrositio (antes estaba duplicado en cada una).
export const fixUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${API_HOST}${url}`;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Si el body es FormData, eliminar el Content-Type para que axios
    // lo detecte automáticamente e incluya el boundary correcto
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Solo redirigir al login si:
    // 1. Hay una respuesta del servidor (no es error de red)
    // 2. El status es 401
    // 3. El error indica específicamente token expirado/inválido
    if (error.response) {
      const status = error.response.status;
      let data = error.response.data;

      // Los endpoints con responseType 'blob' (PDFs, Excel) devuelven el error
      // como Blob, que no tiene .error ni .message. Sin desempaquetarlo, un 401
      // por token vencido no se detecta y el usuario nunca es redirigido al
      // login: solo ve un error genérico y reintenta para siempre.
      // Reescribimos error.response.data con el JSON ya parseado (no solo la
      // variable local `data`) para que el catch del caller — que lee
      // error.response.data.error directo, no vuelve a tocar el blob — reciba
      // el mensaje real del backend en vez de un Blob sin desempaquetar.
      if (data instanceof Blob) {
        try {
          data = JSON.parse(await data.text());
          error.response.data = data;
        } catch {
          // No era JSON: el Blob se DEJA como estaba en `error.response.data`.
          //
          // Pisarlo rompia a quien lo lea sin optional chaining: sobre el Blob
          // `data.error` da `undefined` (es un objeto sin esa propiedad), pero
          // sobre `null` —o sobre `undefined`— tira TypeError. O sea que el
          // interceptor convertia un valor ausente en una excepcion, para un
          // caso que ni siquiera puede parsear. La variable local si queda en
          // `undefined`, que es lo que espera el chequeo de 401 de abajo.
          data = undefined;
        }
      }

      // Solo redirigir si es 401 Y el mensaje indica problema de token
      if (status === 401) {
        const errorMsg = data?.error?.toLowerCase() || data?.message?.toLowerCase() || '';
        const isTokenError = errorMsg.includes('token') || 
                            errorMsg.includes('expirado') || 
                            errorMsg.includes('expired') ||
                            errorMsg.includes('invalid') ||
                            errorMsg.includes('unauthorized');
        
        if (isTokenError) {
          console.warn('Token error, redirigiendo al login...');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ============ AUTH ============
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  registro: (data) => api.post('/auth/registro', data),
  getMe: () => api.get('/auth/me'),
  updatePerfil: (data) => api.put('/auth/perfil', data),
  cambiarPassword: (data) => api.post('/auth/cambiar-password', data),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

// ============ ANIMALES ============
export const animalService = {
  getAll: (params, signal) => api.get('/animales', { params, signal }),
  getById: (id) => api.get(`/animales/${id}`),
  create: (data) => api.post('/animales', data),
  update: (id, data) => api.put(`/animales/${id}`, data),
  delete: (id) => api.delete(`/animales/${id}`),
  getByEvento: (eventoId) => api.get('/animales', { params: { evento: eventoId, per_page: 200 } }),
  // Variantes livianas para la pagina de venta (vista=venta → payload sin genealogia).
  getByEventoVenta: (eventoId) => api.get('/animales', { params: { evento: eventoId, per_page: 200, vista: 'venta' } }),
  getAllVenta: (params) => api.get('/animales', { params: { ...params, vista: 'venta' } }),
  // Solo `{ ids, total }`, en el orden del listado y SIN paginar. Para la
  // navegacion anterior/siguiente de la ficha, que necesita la fila entera y de
  // cada animal usa nada mas que el id. Con vista=venta eso traia la ficha
  // completa de hasta 1000 animales para tirar todo menos el numero.
  getIds: (params) => api.get('/animales', { params: { ...params, vista: 'ids' } }),
  asignarEvento: (animalId, eventoId) => api.post(`/animales/${animalId}/asignar-evento`, { id_evento: eventoId }),
  getSinEvento: (params) => api.get('/animales/sin-evento', { params }),
  asignarEventoMasivo: (id_evento, animales) => api.post('/animales/asignar-evento-masivo', { id_evento, animales }),
  // Saca animales de un remate YA FINALIZADO para poder consignarlos a otro.
  // Reemplaza a `desasignarEvento`, que apuntaba a un endpoint inexistente
  // (`/animales/:id/desasignar-evento`) y nunca lo llamo nadie: era un 404 en
  // espera. Este va en lote a proposito — la base esta en otra region y un
  // request por animal costaba un round-trip cada uno.
  liberarEvento: (animales) => api.post('/animales/liberar-evento', { animales }),
  // Saca animales de su remate aunque esté EN CURSO: lo decide el dueño del
  // animal (o el organizador del remate). Va con `crud_animales`.
  retirarEvento: (animales) => api.post('/animales/retirar-evento', { animales }),
  // Cabaña de PROCEDENCIA de varios animales a la vez.
  //
  // `id_cabana: null` DESASIGNA. `id_cabana` AUSENTE es un 400 — NO son lo
  // mismo, y por eso este metodo pide los dos argumentos siempre: llamarlo con
  // uno solo deja la clave en `undefined`, `JSON.stringify` la borra y el
  // backend rechaza. Falla ruidoso a proposito; tratarlos igual haria que un
  // bug del front borrara cabañas en silencio.
  //
  // El front necesita ADEMAS `ver_cabanas` para poder listar las opciones; el
  // endpoint en si pide `crud_animales`.
  //
  // Antes solo se podia de a uno en la ficha o por el CSV, asi que un stock ya
  // cargado no tenia forma de moverse: Pilaga tenia 309 animales sin cabaña. Y
  // sin esto la restriccion por cabaña de una invitacion no matchea nada.
  asignarCabanaMasivo: (animales, id_cabana) =>
    api.post('/animales/asignar-cabana', { animales, id_cabana }),
  getGenealogia: (id) => api.get(`/animales/${id}/genealogia`),
  saveGenealogia: (id, data) => api.post(`/animales/${id}/genealogia`, data),
};

// ============ EVENTOS ============
export const eventoService = {
  getAll: (params) => api.get('/eventos', { params }),
  // Lista liviana: id+nombre+slug+estado_evento+fechas+`es_finalizado`. Para
  // selectores de evento, donde getAll cargaba ~30 campos innecesariamente.
  //
  // `es_finalizado` viene YA RESUELTO por el backend — NO lo recalcules acá.
  // Un remate esta finalizado por cierre manual O por fecha vencida, y el back
  // corre en UTC mientras el navegador esta en UTC-3: rehacer esa cuenta en el
  // front diverge tres horas por noche. Subdeclarar este payload fue lo que
  // llevo a re-derivarlo una vez; por eso el campo figura aca.
  //
  // `?estado=1` devuelve solo los activos NO vencidos, y `?estado=0` su
  // complemento. Sin parametro vienen todos, que es lo que necesita cualquier
  // pantalla que trabaje sobre remates cerrados.
  //
  // DEVUELVE `{ eventos, total }`, Y `total` NO ES DECORATIVO.
  // El endpoint corta en 200 filas (`.limit(200)`, evento_routes.py). `total`
  // es el conteo SIN ese tope, y viene justamente para que el consumidor pueda
  // darse cuenta de que la lista llego cortada. Sin ese chequeo, pasado el tope
  // el remate mas nuevo desaparece del selector y no hay ninguna señal: la
  // request devolvio 200 con una pagina incompleta.
  //
  // NO DESEMPAQUETES `res.data.eventos` A MANO. Pasa la respuesta por
  // `eventosDeListaCorta(res)` (utils/listaCorta.js): devuelve el array y avisa
  // si vino cortada. Los once consumidores lo usan; hacerlo a mano es volver a
  // tirar el `total` que el backend paga con un count() en cada request.
  //
  // Se declara aca y completo a proposito — subdeclarar este payload ya llevo
  // una vez a re-derivar en el front algo que el backend mandaba servido.
  getListaCorta: (params) => api.get('/eventos/lista-corta', { params }),
  // Remates de OTRAS cabanias a las que me invitaron a consignar. La
  // contracara de `/eventos/<id>/invitados`, que lista a quien invite yo.
  //
  // Devuelve `{invitaciones, mis_cabanas}`:
  //   - cada invitacion trae `cabanas_elegidas`, que son OBJETOS
  //     `{id_cabana, nom_cabana, activa}` y **NO ids**. El nombre viene servido
  //     a proposito: NO lo cruces contra `mis_cabanas`, que filtra las dadas de
  //     baja, y una unidad de baja puede seguir participando de un remate ya
  //     arrancado — cruzando ids esa baja le sale al usuario como un `#7`.
  //     Ojo con la asimetria: el REQUEST de `responder` si lleva ids pelados.
  //     **VACIO significa "participo con TODAS", no "con ninguna"** — es la
  //     regla de `evento_invitado_cabana` y mostrarlo como vacio asusta de gusto.
  //   - `mis_cabanas` son MIS unidades productivas activas, para poder elegir
  //     al aceptar. Vienen al nivel de la respuesta y no repetidas en cada
  //     invitacion: son las mismas para todas.
  //
  // Se declara completo a proposito: subdeclarar un payload es lo que ya llevo
  // una vez a re-derivar en el front algo que el backend mandaba servido.
  getMisInvitaciones: () => api.get('/eventos/mis-invitaciones'),
  // El INVITADO acepta o rechaza. Es lo que abre la pared: la invitacion
  // nace pendiente y sin esto el titular no gana ningun acceso.
  // `cabanas` es OPCIONAL y sólo aplica al aceptar: son las unidades
  // productivas con las que participo. **Omitirla (o mandarla vacía) significa
  // participar con TODAS**, que es lo que necesita el caso simple —una empresa,
  // una cabaña— y lo que mantiene válidas las invitaciones anteriores. La
  // ausencia de restricción NO es una restricción vacía.
  responderInvitacion: (idEventoInvitado, respuesta, cabanas) =>
    api.post(`/eventos/mis-invitaciones/${idEventoInvitado}/responder`,
             cabanas?.length ? { respuesta, cabanas } : { respuesta }),
  // Y cierra la que ya habia aceptado. La revocacion es bidireccional: la
  // invitacion abre los animales DEL INVITADO, asi que el tiene que poder
  // cerrarla.
  revocarMiInvitacion: (idEventoInvitado) =>
    api.post(`/eventos/mis-invitaciones/${idEventoInvitado}/revocar`),
  // El TITULAR revoca una invitacion que el emitio. La revocacion es
  // bidireccional: los dos lados pueden cerrar la puerta.
  revocarInvitado: (idEvento, idClientes) =>
    api.delete(`/eventos/${idEvento}/invitados/${idClientes}`),
  // A quien invite YO. Contracara de getMisInvitaciones, que lista donde me
  // invitaron a mi. Vive aca y no en cabanias porque una invitacion NO es una
  // cabania: es un permiso entre dos empresas.
  getInvitacionesEnviadas: () => api.get('/eventos/invitaciones-enviadas'),
  invitarCabana: (idEvento, idClientes, alias) =>
    api.post(`/eventos/${idEvento}/invitados`, {
      id_clientes_invitado: idClientes, alias,
    }),
  // Cuantos animales de esa cabania ya estan en el remate. Alimenta el
  // aviso de "si cambias el alias, las planillas que te queden tienen que
  // usar el nuevo" — que se muestra ANTES de confirmar, no despues.
  animalesCargadosDeInvitada: (idEvento, idClientes) =>
    api.get(`/eventos/${idEvento}/invitados/${idClientes}/animales-cargados`),
  getById: (id) => api.get(`/eventos/${id}`, { params: { micrositio: true } }),
  getActivos: () => api.get('/eventos', { params: { estado: 1 } }),
  getLimites: () => api.get('/eventos/limites'),
  create: (data) => api.post('/eventos', data),
  update: (id, data) => api.put(`/eventos/${id}`, data),
  delete: (id) => api.delete(`/eventos/${id}`),
  finalizar: (id) => api.post(`/eventos/${id}/finalizar`),
  reactivar: (id) => api.post(`/eventos/${id}/reactivar`),
  getAnimales: (id, params) => api.get(`/eventos/${id}/animales`, { params }),
  getEstadisticas: (id) => api.get(`/eventos/${id}/estadisticas`),
  asignarAnimales: (id, animales) => api.post(`/eventos/${id}/asignar-animales`, { animales }),
  exportarGanadoresExcel: (id) => api.get(`/eventos/${id}/ofertas-ganadoras/excel`, { responseType: 'blob' }),
  // Micrositio
  getMicrositioConfig: (id) => api.get(`/eventos/${id}/micrositio`),
  updateMicrositioConfig: (id, config) => api.put(`/eventos/${id}/micrositio`, config),
  resetMicrositioConfig: (id) => api.post(`/eventos/${id}/micrositio/reset`),
  getMicrositioCssUrl: (id) => `${API_HOST}/api/eventos/${id}/micrositio/css`,
};

// ============ CLIENTES ============
export const clienteService = {
  // Resuelve un CUIT EXACTO (11 digitos) a la cabania que lo tiene, para
  // invitarla a un remate. Un fragmento devuelve 400 y una cabania inactiva
  // responde igual que una inexistente: sin eso el endpoint seria un
  // directorio recorrible de los clientes de Puja.
  buscarPorCuit: (cuit) => api.get('/clientes/buscar-por-cuit', { params: { cuit } }),
  getAll: (params) => api.get('/clientes', { params }),
  getById: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  delete: (id) => api.delete(`/clientes/${id}`),
  getEventos: (id) => api.get(`/clientes/${id}/eventos`),
  getAnimales: (id) => api.get(`/clientes/${id}/animales`),
};

// ============ CABAÑAS ============
export const cabanaService = {
  // Las cabanias que veo: las MIAS + las de quienes consignan en mis remates.
  // Devuelve CABAÑAS y no tenants: al reparentar la tabla, cada cabania paso a
  // ser de su dueño, y un tenant puede tener varias (Santa Juana / El Sendero).
  getRelacionadas: () => api.get('/cabanas/relacionadas'),
  getAll: (params) => api.get('/cabanas', { params }),
  getActivas: () => api.get('/cabanas/activas'),
  getById: (id) => api.get(`/cabanas/${id}`),
  create: (data) => api.post('/cabanas', data),
  update: (id, data) => api.put(`/cabanas/${id}`, data),
  // Como se PRESENTA la cabaña: el codigo corto de la columna CABANA del CSV
  // (`alias`) y la marca publica (`nombre_publico`), y nada mas. Va aparte de
  // `update` porque lo pueden tocar dos que no pueden editar la cabaña: la
  // dueña con plan de solo lectura (Event invitado) y el organizador que la
  // tiene invitada con la invitacion activa. Solo viaja lo que se manda; vacio
  // = sin valor. Devuelve `en_csv` (lo que hay que escribir) y `nombre_publico`.
  // Desde el 2026-10-29 tambien el LOGO (`logo_cabana`: la ruta que devolvio
  // `uploadLogo`, vacio = quitarlo) y su fondo en los PDF (`logo_fondo`:
  // 'ninguno' | 'oscuro'): es la firma del catalogo y las caratulas,
  // presentacion pura, y hasta ese dia no se podia cargar desde el panel.
  editarPresentacion: (id, { alias, nombre_publico, logo_cabana, logo_fondo } = {}) =>
    api.patch(`/cabanas/${id}/alias`, {
      ...(alias !== undefined ? { alias } : {}),
      ...(nombre_publico !== undefined ? { nombre_publico } : {}),
      ...(logo_cabana !== undefined ? { logo_cabana } : {}),
      ...(logo_fondo !== undefined ? { logo_fondo } : {}),
    }),
  delete: (id) => api.delete(`/cabanas/${id}`),
  getAnimales: (id, params) => api.get(`/cabanas/${id}/animales`, { params }),
  // Sube el archivo y devuelve { logo_cabana: '/static/uploads/cabanas/...',
  // logo_claro: true|false|null } para guardar después con update o con
  // editarPresentacion. `logo_claro` es una SUGERENCIA (un trazo blanco sobre
  // transparente): el modal preselecciona la contención y el productor decide.
  // Va separado del alta porque al crear una cabaña nueva todavía no hay id al
  // que colgarle el archivo. El interceptor saca el Content-Type para que
  // axios ponga el boundary.
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/cabanas/upload-logo', fd);
  },
};

// ============ DASHBOARD ============
// Endpoint BFF dedicado al Dashboard: devuelve counts + top 5 eventos +
// top 5 ultimos animales en 1 sola request (en vez de 2 fetches al
// homologo viejo que serializaba).
export const dashboardService = {
  getStats: () => api.get('/dashboard/stats'),
};

// ============ CATEGORIAS ============
export const categoriaService = {
  getAll: () => api.get('/categorias'),
  // Solo categorias del tenant que tienen al menos 1 animal cargado.
  getConAnimales: () => api.get('/categorias/con-animales'),
  // Solo categorias con animales en un evento especifico.
  getConAnimalesEnEvento: (idEvento) => api.get(`/categorias/evento/${idEvento}/con-animales`),
  // Solo categorias con ofertas en un evento especifico.
  getConOfertasEnEvento: (idEvento) => api.get(`/categorias/evento/${idEvento}/con-ofertas`),
  getById: (id) => api.get(`/categorias/${id}`),
  create: (data) => api.post('/categorias', data),
  update: (id, data) => api.put(`/categorias/${id}`, data),
  delete: (id) => api.delete(`/categorias/${id}`),
};

// ============ RAZAS ============
export const razaService = {
  getAll: () => api.get('/razas'),
  create: (data) => api.post('/razas', data),
  update: (id, data) => api.put(`/razas/${id}`, data),
  delete: (id) => api.delete(`/razas/${id}`),
};

// ============ OFERTAS ============
export const ofertaService = {
  getAll: (params) => api.get('/ofertas', { params }),
  getByEvento: (eventoId) => api.get('/ofertas', { params: { evento: eventoId, per_page: 500 } }),
  getByAnimal: (animalId) => api.get(`/ofertas/animal/${animalId}`),
  create: (data) => api.post('/ofertas', data),
  // Seguimiento (favoritos)
  getMisSeguimientos: (eventoId) => api.get('/ofertas/seguimiento', { params: { evento: eventoId } }),
  addSeguimiento: (animalId) => api.post('/ofertas/seguimiento', { id_animal: animalId }),
  removeSeguimiento: (animalId) => api.delete(`/ofertas/seguimiento/${animalId}`),
};

// ============ USUARIOS ============
export const usuarioService = {
  getAll: (params) => api.get('/usuarios', { params }),
  getById: (id) => api.get(`/usuarios/${id}`),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`),
  getRoles: () => api.get('/usuarios/roles'),
  // Gestión de clientes (usuarios rol 3)
  getMisClientes: (params) => api.get('/usuarios/mis-clientes', { params }),
  createCliente: (data) => api.post('/usuarios/crear-cliente', data),
  // Carga masiva de clientes
  descargarPlantillaClientes: () => api.get('/usuarios/plantilla-clientes', { responseType: 'blob' }),
  cargarClientesMasivo: (formData, onUploadProgress) => api.post('/usuarios/cargar-clientes-masivo', formData, {
    onUploadProgress
  }),
};

// ============ FAMILIARES ============
// Modelo: catálogo (familiares) + vinculación N:M (animal_familiar)
// Backend: backend/app/routes/familiar_routes.py
export const familiarService = {
  // --- CATÁLOGO ---
  // params: { page, per_page, search }
  getAll:   (params)     => api.get('/familiares', { params }),
  getById:  (id)         => api.get(`/familiares/${id}`),
  create:   (data)       => api.post('/familiares', data),   // FormData o JSON; foto opcional
  update:   (id, data)   => api.put(`/familiares/${id}`, data),
  delete:   (id)         => api.delete(`/familiares/${id}`), // cascade a vinculaciones

  // Carga masiva del catálogo (planilla en pantalla)
  // body: { familiares: [{rp_familiar, nom_familiar, premio_familiar?}, ...] }
  // all-or-nothing: si una fila falla, no se inserta nada y se devuelve `errores: [...]`
  cargarCatalogoMasivo: (familiares) =>
    api.post('/familiares/catalogo-masivo', { familiares }),

  // Carga masiva de fotos (match filename ↔ rp_familiar dentro del tenant)
  // El interceptor maneja el boundary del multipart. El backend espera
  // el campo 'fotos' (NO 'files') — ver familiar_routes.py → /fotos-masivo.
  // Sin número de línea a propósito: las referencias por línea se pudren en
  // el primer refactor del archivo.
  cargarFotosMasivo: (files, onUploadProgress) => {
    const fd = new FormData();
    files.forEach(f => fd.append('fotos', f.originFileObj || f));
    return api.post('/familiares/fotos-masivo', fd, { onUploadProgress });
  },

  // --- VINCULACIÓN (animal ↔ familiar) ---
  // Devuelve { familiares: [...vinculaciones...], total }
  getVinculacionesByAnimal: (animalId) =>
    api.get(`/familiares/animal/${animalId}`),

  // body: { id_familiar, vinculo_familiar?, orden_vista? }
  vincular: (animalId, data) =>
    api.post(`/familiares/animal/${animalId}/vincular`, data),

  // body: { vinculo_familiar?, orden_vista? }
  actualizarVinculacion: (animalId, idAf, data) =>
    api.put(`/familiares/animal/${animalId}/vincular/${idAf}`, data),

  desvincular: (animalId, idAf) =>
    api.delete(`/familiares/animal/${animalId}/vincular/${idAf}`),

  // --- VINCULACIÓN POR LOTE (pantalla de vinculación masiva) ---
  // Los de arriba son de a uno y los usa la pestaña Familiares de la ficha.
  // Estos tres son para cargar un remate entero sin entrar y salir de la ficha
  // una vez por animal, que es donde estaba el 100% del tiempo perdido.

  // Arma la pantalla entera en UN request: animales del evento con sus
  // vinculaciones, catálogo de familiares y etiquetas de vínculo ya usadas.
  getDatosVinculacionMasiva: (idEvento) =>
    api.get('/familiares/vinculacion-masiva', { params: { id_evento: idEvento } }),

  // Reemplaza el set COMPLETO de un animal. El orden del array ES el orden_vista.
  // body: { vinculaciones: [{ id_familiar, vinculo_familiar? }, ...] }
  // Una lista vacía desvincula todo (caso válido).
  reemplazarVinculaciones: (animalId, vinculaciones) =>
    api.put(`/familiares/animal/${animalId}/vinculaciones`, { vinculaciones }),

  // body: { id_animal_origen, destinos: [id_animal...], modo: 'sumar'|'reemplazar' }
  // 'sumar' es idempotente: repetirlo no duplica.
  copiarVinculaciones: (data) =>
    api.post('/familiares/copiar-vinculaciones', data),
};

// ============ IMÁGENES ============
export const imagenService = {
  getByAnimal: (animalId) => api.get(`/imagenes/animal/${animalId}`),
  // BAJA de `upload`: apuntaba a `POST /imagenes/upload`, que NO EXISTE en
  // imagen_routes.py, y no lo llamaba nadie. Un 404 en espera, igual que
  // `desasignarEvento`. El alta de imagen de un animal va por `uploadAnimal`.
  getAll: (params) => api.get('/imagenes', { params }),

  // COMO SE USA una foto: su punto focal, si va al carrusel de la ficha y si es
  // la portada. NO toca el archivo — son decisiones de presentacion sobre una
  // imagen que ya existe, y por eso van juntas en un solo PUT.
  //
  // Marcar una portada DESMARCA la anterior del mismo animal: lo hace el
  // backend en la misma transaccion, porque la base tiene un indice unico
  // parcial que solo admite una.
  actualizarPresentacion: (idImagen, cambios) =>
    api.put(`/imagenes/${idImagen}`, cambios),
  getEstadisticas: () => api.get('/imagenes/estadisticas'),
  uploadBulk: (files, onUploadProgress) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return api.post('/imagenes/upload-masivo', formData, {
      onUploadProgress
    });
  },
  // UNA FOTO O VARIAS. Acepta un File suelto o un array, para no obligar a
  // cambiar los llamadores que ya mandan uno.
  //
  // Manda el campo como `files` (plural) y el backend acepta las dos formas:
  // asi el front y el backend pueden deployarse por separado sin que quede una
  // ventana donde la subida no funciona.
  uploadAnimal: (animalId, archivos) => {
    const lista = Array.isArray(archivos) ? archivos : [archivos];
    const formData = new FormData();
    lista.forEach((f) => formData.append('files', f));
    return api.post(`/imagenes/upload-animal/${animalId}`, formData);
  },
  // `uploadTemp` (POST /imagenes/upload-temp) se BORRO el 2026-10-29: ese
  // endpoint nunca existio y la subida de portadas y publicidades del
  // asistente daba 404 siempre. Ahora va por `pdfService.subirArchivo`.
  delete: (id) => api.delete(`/imagenes/${id}`),
  // BAJA de `setPrincipal`: apuntaba a `POST /imagenes/:id/set-principal`, que
  // nunca existio, y no lo llamaba nadie.
  //
  // LA FEATURE SI SE CONSTRUYO, con otro nombre y otra forma: la portada hoy es
  // `es_portada` y se marca con `actualizarPresentacion` (`PUT /imagenes/:id`),
  // que ademas resuelve el carrusel y el punto focal. Lo que se abandono fue
  // ESTE camino, no la idea — y el criterio de cual foto es la portada vive en
  // `utils/portadaAnimal.js`, compartido con el backend.
};

// ============ CARGA MASIVA ============
export const cargaMasivaService = {
  // Plantilla DEL REMATE: trae una fila de ejemplo por cabania invitada con
  // lo que hay que escribir en CABANA (su codigo corto, o el nombre si no
  // tiene), para que el productor no tenga que acordarse.
  descargarPlantillaRemate: (idEvento) =>
    api.get(`/carga-masiva/plantilla-remate/${idEvento}`, { responseType: 'blob' }),
  descargarPlantilla: () => api.get('/carga-masiva/plantilla', { responseType: 'blob' }),
  // Con `idEvento`, la columna CABANA del CSV nombra la CABAÑA de cada animal
  // (su codigo corto o su nombre, de las invitadas o las mias), y el animal
  // nace ya asignado a ese remate y a su dueño.
  // Sin `idEvento` es la carga de siempre: mi propia hacienda, sin remate. La
  // columna CABANA es OPCIONAL ahi (desde 2026-10-05) y elige entre MIS
  // cabañas; con una sola activa se asigna sola.
  cargarAnimales: (file, onUploadProgress, idEvento) => {
    const formData = new FormData();
    formData.append('file', file);
    if (idEvento) formData.append('id_evento', idEvento);
    return api.post('/carga-masiva/animales', formData, {
      onUploadProgress
    });
  },
  // Lleva el MISMO `idEvento` que `cargarAnimales`: validar es la promesa de
  // lo que va a pasar al cargar, y la columna CABANA se resuelve contra
  // cabañas distintas con remate (las invitadas y las mias) y sin el (solo
  // las mias).
  validarCSV: (file, idEvento) => {
    const formData = new FormData();
    formData.append('file', file);
    if (idEvento) formData.append('id_evento', idEvento);
    return api.post('/carga-masiva/validar-csv', formData);
  },
};

// ============ RECURSOS Y PERMISOS ============
export const recursoService = {
  // Para el usuario actual
  getMisRecursos: () => api.get('/recursos/mis-recursos'),
  verificarRecurso: (codigo) => api.get(`/recursos/verificar/${codigo}`),
  
  // Para administración
  getAll: (params) => api.get('/recursos', { params }),
  getModulos: () => api.get('/recursos/modulos'),
  getRecursosMembresia: (idMembresia) => api.get(`/recursos/membresia/${idMembresia}`),
  toggleRecurso: (idMembresia, idRecurso, activo) => 
    api.post(`/recursos/membresia/${idMembresia}/toggle`, { id_recurso: idRecurso, activo }),
  setRecursosBulk: (idMembresia, recursos) => 
    api.post(`/recursos/membresia/${idMembresia}/bulk`, { recursos }),
};

// ============ IDENTIDAD VISUAL (CATÁLOGO) ============
export const identidadService = {
  getMiIdentidad: () => api.get('/identidad/mi-identidad'),
  getById: (id) => api.get(`/identidad/${id}`),
  create: (data) => api.post('/identidad', data),
  update: (id, data) => api.put(`/identidad/${id}`, data),
  delete: (id) => api.delete(`/identidad/${id}`),
  // URL para cargar el CSS generado en el micrositio
  getCssUrl: (id) => `${API_HOST}/api/identidad/generar-css/${id}`,
};

// ============ MICROSITIO PÚBLICO ============
export const micrositioService = {
  getBySlug: (slug) => api.get(`/micrositio/${slug}`),
  // `animalSlug` es el slug COMPUESTO del animal (ej. "42-mi-toro"), no el id
  // pelado: el backend parsea el id de ahi. Se llamaba `idAnimal` y mentia.
  getAnimal: (slug, animalSlug) => api.get(`/micrositio/${slug}/animal/${animalSlug}`),
};

// ============ CRÍAS ============
export const criaService = {
  getByAnimal: (idAnimal)              => api.get(`/crias/animal/${idAnimal}`),
  // `{ crias: [...] }`: TODAS las crías del animal en ese remate (2026-10-21,
  // las elecciones). Sin crías es una lista vacía, no un 404.
  getByAnimalEvento: (idAnimal, idEvento) => api.get(`/crias/animal/${idAnimal}/evento/${idEvento}`),
  // REEMPLAZA el set completo (lo que guarda la planilla de la solapa «Crías»).
  // Todo o nada: con una fila mala vuelve 400 con `errores: [{fila, error}]`.
  reemplazar: (idAnimal, idEvento, crias) => api.put(`/crias/animal/${idAnimal}/evento/${idEvento}`, { crias }),
  create: (idAnimal, data)             => api.post(`/crias/animal/${idAnimal}`, data),
  update: (idCria, data)               => api.put(`/crias/${idCria}`, data),
  delete: (idCria)                     => api.delete(`/crias/${idCria}`),
};

// ============ SPONSORS ============
export const sponsorService = {
  getAll: (params) => api.get('/sponsors', { params }),
  create: (data) => api.post('/sponsors', data),
  update: (id, data) => api.put(`/sponsors/${id}`, data),
  delete: (id) => api.delete(`/sponsors/${id}`),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/sponsors/upload-logo', fd);
  },
};

// ============ ANALYTICS ============
export const analyticsService = {
  // `id_visitante`: el UUID anónimo del navegador (utils/visitante.js), con el
  // que el tablero cuenta visitantes únicos. Va en los DOS orígenes.
  track: (tipo, id_evento, extras = {}) =>
    api.post('/analytics/track', { tipo, id_evento, id_visitante: idVisitante(), ...extras }),

  // El catalogo de genetica usa LOS MISMOS tipos que el remate: la fila de
  // analytics cuelga de un evento O de un catalogo, y esa columna ya dice de
  // donde viene. Tipos propios —`visita_catalogo_animal`— obligarian a
  // duplicar cada filtro de reporte para siempre.
  //
  // Va como funcion aparte y no como tercer parametro opcional de `track`
  // porque el origen es excluyente, no un extra: el backend rechaza con 400
  // que vengan los dos.
  trackCatalogo: (tipo, id_catalogo_genetica, extras = {}) =>
    api.post('/analytics/track', { tipo, id_catalogo_genetica, id_visitante: idVisitante(), ...extras }),
  getStats: (eventoId) => api.get(`/analytics/evento/${eventoId}`),
  // Metricas de un catalogo de genetica. Sin figura del invitado: un
  // catalogo es de un solo cliente, asi que o es tuyo o da 403/404.
  getStatsCatalogo: (catalogoId, dias = 30) =>
    api.get(`/analytics/catalogo/${catalogoId}`, { params: { dias } }),
  getTablero: (eventoId, dias = 30) => api.get('/analytics/tablero', { params: { evento_id: eventoId, dias } }),
  getCompleto: (eventoId, tipo) => api.get('/analytics/completo', { params: { evento_id: eventoId, tipo } }),
  exportarExcel: (eventoId, tipo) => api.get('/analytics/completo/exportar', {
    params: { evento_id: eventoId, tipo },
    responseType: 'blob',
  }),
  // El tablero entero (visitas + ventas por categoría), no una tabla puntual.
  // Lleva `dias` para que el PDF diga lo mismo que la pantalla desde la que se
  // exportó.
  exportarTableroPdf: (eventoId, dias = 30) => api.get('/analytics/tablero/exportar-pdf', {
    params: { evento_id: eventoId, dias },
    responseType: 'blob',
  }),
  exportarPdf: (eventoId, tipo) => api.get('/analytics/completo/exportar-pdf', {
    params: { evento_id: eventoId, tipo },
    responseType: 'blob',
  }),
};

// ============ CONDICIONES ============
export const condicionesService = {
  getByEvento: (eventoId) => api.get(`/condiciones/evento/${eventoId}`),
  update: (eventoId, data) => api.put(`/condiciones/evento/${eventoId}`, data),
};

// ============ PDF ============
export const pdfService = {
  // `vista`: 'categoria' (una sección por categoría y raza, la de siempre) o
  // 'corral' (una por corral). Sin vista el backend da la de categoría.
  getSecciones: (eventoId, vista) =>
    api.get(`/pdf/secciones/${eventoId}`, { params: vista ? { vista } : undefined }),
  // Por categoría y raza, o por corral. Axios OMITE los params en null, y
  // «sin corral» es un filtro real: viaja como la cadena 'null'.
  getAnimalesSeccion: (eventoId, { id_categoria, id_raza, corral }) =>
    api.get(`/pdf/secciones/${eventoId}/animales`, {
      params: corral !== undefined
        ? { corral: corral === null ? 'null' : corral }
        : { id_categoria, id_raza },
    }),
  // Imagen o PDF para una portada o publicidad (hasta 60 MB; un PDF vuelve
  // como la URL de su primera pagina en JPEG). Reemplaza al `upload-temp`
  // que nunca existio.
  subirArchivo: (file) => {
    const formData = new FormData();
    formData.append('archivo', file);
    return api.post('/pdf/archivos', formData);
  },
  // La maquetacion guardada del asistente, una por (evento, modo), que vive
  // siete dias desde el ultimo guardado. 404 `SIN_MAQUETACION` si no hay.
  getMaquetacion: (eventoId, modo) => api.get(`/pdf/maquetacion/${eventoId}`, { params: { modo } }),
  guardarMaquetacion: (eventoId, data) => api.put(`/pdf/maquetacion/${eventoId}`, data),
  borrarMaquetacion: (eventoId, modo) => api.delete(`/pdf/maquetacion/${eventoId}`, { params: { modo } }),
  generar: (data) => api.post('/pdf/generar', data, {
    responseType: 'blob',
    // 5 min: el modal avisa "entre 2 y 3 minutos", asi que un timeout de 3
    // abortaba justo en el peor caso anunciado, con el backend todavia
    // generando. Debe quedar por encima de lo que la UI promete.
    timeout: 300000,
  }),
};

// ============ VENTAS DE ANIMALES ============
export const ventaService = {
  listar: (params) => api.get('/ventas/', { params }),
  crear: (data) => api.post('/ventas/', data),
  crearCorral: (data) => api.post('/ventas/corral', data),
  editar: (id, data) => api.put(`/ventas/${id}`, data),
  eliminar: (id) => api.delete(`/ventas/${id}`),
  exportarExcel: (idEvento) => api.get('/ventas/exportar', {
    params: idEvento ? { id_evento: idEvento } : {},
    responseType: 'blob',
  }),
  getTablero: (idEvento) => api.get('/ventas/tablero', {
    params: idEvento ? { id_evento: idEvento } : {},
  }),
  getSociedad: () => api.get('/ventas/sociedad'),
  detalle: (id) => api.get(`/ventas/${id}`),
  registroRapido: (data) => api.post('/ventas/registro-rapido', data),
  buscarUsuarios: (q) => api.get('/ventas/buscar-usuarios', { params: { q } }),
};

// ============ VENTA TRANSITORIA (remate en vivo) ============
export const ventaTransitoriaService = {
  // Precarga de la grilla: { animales: [...identidad], asientos: [...cargados] }
  precargaEvento: (idEvento) => api.get(`/ventas-transitorias/evento/${idEvento}`),
  crear: (data) => api.post('/ventas-transitorias/', data),
  editar: (id, data) => api.put(`/ventas-transitorias/${id}`, data),
  eliminar: (id) => api.delete(`/ventas-transitorias/${id}`),
  // Match / reconciliación
  pendientes: (idEvento) => api.get(`/ventas-transitorias/evento/${idEvento}/pendientes`),
  reconciliar: (data) => api.post('/ventas-transitorias/reconciliar', data),
  // Resumen por categoría + detalle, en un solo archivo.
  exportarExcel: (idEvento) => api.get(`/ventas-transitorias/evento/${idEvento}/exportar`, {
    responseType: 'blob',
  }),
  exportarPdf: (idEvento) => api.get(`/ventas-transitorias/evento/${idEvento}/exportar-pdf`, {
    responseType: 'blob',
  }),
};

// ============ MOVIMIENTOS (EMBRIONES / SEMEN) ============
export const movimientoService = {
  getHembra: (id_animal) => api.get(`/movimientos/hembra/${id_animal}`),
  crearHembra: (data) => api.post('/movimientos/hembra/', data),
  getMacho: (id_animal) => api.get(`/movimientos/macho/${id_animal}`),
  crearMacho: (data) => api.post('/movimientos/macho/', data),
  subirDocumento: (formData) => api.post('/movimientos/documento', formData),
  getDocumentos: (tipo, id) => api.get(`/movimientos/documentos/${tipo}/${id}`),
};

// ============ PANEL COMPRADOR (rol=3) ============
export const buyerService = {
  // animales en sociedad
  getMisAnimales:        ()       => api.get('/cliente/mis-animales'),
  getMovimientosAnimal:  (id)     => api.get(`/cliente/mis-animales/${id}/movimientos`),
  // remates
  getRemates:            ()       => api.get('/cliente/remates'),
  // ofertas y seguimiento
  getMisOfertas:         ()       => api.get('/cliente/mis-ofertas'),
  getMisSeguimientos:    ()       => api.get('/cliente/mis-seguimientos'),
  // autorizaciones
  getAutorizaciones:     ()       => api.get('/cliente/autorizaciones'),
  solicitarAutorizacion: (id_c)   => api.post('/cliente/autorizaciones', { id_clientes: id_c }),
  // condiciones del remate — son POR EVENTO, se aceptan antes de la 1ra oferta
  getCondiciones:        (id_ev)  => api.get(`/cliente/condiciones/${id_ev}`),
  aceptarCondiciones:    (id_ev)  => api.post(`/cliente/condiciones/${id_ev}/aceptar`),
  // estado del comprador frente a un remate (habilitación + condiciones), en
  // una sola llamada, para pintar el botón de pre-oferta como corresponde
  getAccesoRemate:       (id_ev)  => api.get(`/cliente/acceso-remate/${id_ev}`),
  // tickets
  getTickets:            ()       => api.get('/cliente/tickets'),
  crearTicket:           (data)   => api.post('/cliente/tickets', data),
  getTicket:             (id)     => api.get(`/cliente/tickets/${id}`),
  responderTicket:       (id, m)  => api.post(`/cliente/tickets/${id}/mensajes`, { mensaje: m }),
  subirImagenesTicket:   (id, files) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f.originFileObj || f));
    return api.post(`/cliente/tickets/${id}/imagenes`, fd);
  },
  // notificaciones
  getNotificaciones:     ()       => api.get('/cliente/notificaciones'),
  getNoLeidasCount:      ()       => api.get('/cliente/notificaciones/no-leidas-count'),
  marcarLeida:           (id)     => api.put(`/cliente/notificaciones/${id}/leer`),
  marcarTodasLeidas:     ()       => api.put('/cliente/notificaciones/leer-todas'),
  // admin — autorizaciones y tickets
  getAutorizacionesPend: ()        => api.get('/admin/autorizaciones-pendientes'),
  responderAutorizacion: (id, e)   => api.put(`/admin/autorizaciones/${id}`, { estado: e }),
  getTicketsAdmin:       (canal)   => api.get('/admin/tickets', { params: { canal } }),
  actualizarTicket:      (id, d)   => api.put(`/admin/tickets/${id}`, d),
  crearTicketPuja:       (data)    => api.post('/admin/tickets', data),
  crearTicketComprador:  (data)    => api.post('/admin/tickets', { ...data, destinatario_tipo: 'comprador' }),
  responderTicketAdmin:  (id, m)   => api.post(`/cliente/tickets/${id}/mensajes`, { mensaje: m }),
  getCompradores:        ()        => api.get('/admin/compradores'),
  // Devuelve la clave temporal UNA sola vez: no se guarda en claro en ningún
  // lado, así que si el modal se cierra sin copiarla hay que blanquear de nuevo.
  blanquearPassword:     (id)      => api.post(`/admin/compradores/${id}/blanquear-password`),
};

// ============ PUJA ADMIN ============
export const pujaService = {
  getTickets:    (filtros) => api.get('/puja/tickets', { params: filtros }),
  getTicket:     (id)      => api.get(`/puja/tickets/${id}`),
  responder:     (id, m)   => api.post(`/puja/tickets/${id}/mensajes`, { mensaje: m }),
  actualizar:    (id, d)   => api.put(`/puja/tickets/${id}`, d),
};

// ============ VIDEOS (codigo_video por animal) ============
export const videoService = {
  // Grid: { animales: [{id_animal, corral, rp_animal, nom_animal, agrupado, codigo_video}] }
  precargaEvento: (idEvento) => api.get(`/videos/evento/${idEvento}`),
  // Grid: guarda varios → { items: [{id_animal, codigo_video}] }
  bulk: (items) => api.post('/videos/bulk', { items }),
  csvValidar: (file, idEvento, sobrescribir = false) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('id_evento', idEvento);
    fd.append('sobrescribir', sobrescribir ? '1' : '0');
    return api.post('/videos/csv/validar', fd);
  },
  csvCargar: (file, idEvento, sobrescribir = false) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('id_evento', idEvento);
    fd.append('sobrescribir', sobrescribir ? '1' : '0');
    return api.post('/videos/csv/cargar', fd);
  },
};

// ============ CATALOGO DE GENETICA (micrositio PUBLICO) ============
//
// Sin token, y esa es toda la gracia: es lo que ve el comprador que llega por
// el link. Cuelga de `/micrositio` y no de `/catalogo-genetica` porque comparte
// naturaleza con el micrositio del remate —publico, cacheable, con rate limit—
// y no con el panel, que es JWT + tenant + recurso en cada ruta.
export const catalogoPublicoService = {
  // Un catalogo APAGADO devuelve 404 y no 403: un 403 confirmaria que existe y
  // solo esta apagado, y el productor que todavia no publico no tiene por que
  // exponer eso.
  get: (slug) => api.get(`/micrositio/genetica/${slug}`),

  // El listado COMPLETO de una seccion. La portada manda solo los destacados y
  // el total; esto es el otro lado, para la pagina "ver todos". `clave` es
  // 'toros' u 'hembras' — cualquier otra cosa el backend la contesta 404.
  getSeccion: (slug, clave) =>
    api.get(`/micrositio/genetica/${slug}/seccion/${clave}`),

  // La ficha de UN animal. `animalSlug` es `{id}-{nombre}` — el id ADELANTE,
  // igual que en el micrositio del remate: el backend lo saca con
  // `split('-')[0]`, asi que con el id al final no encontraria nunca al animal.
  // El slug lo arma el backend y viaja en cada fila de la portada; no se
  // construye aca para que haya UN solo lugar donde se decide su formato.
  getAnimal: (slug, animalSlug) =>
    api.get(`/micrositio/genetica/${slug}/animal/${animalSlug}`),

  // El comprador deja sus datos para que la cabaña lo contacte. Con
  // `canal_consultas: 'whatsapp'` esto viaja ADEMAS de abrir WhatsApp (no en
  // su lugar): asi la consulta queda en el panel aunque el comprador abra
  // WhatsApp y no llegue a escribir nada.
  consultar: (slug, body) => api.post(`/micrositio/genetica/${slug}/consulta`, body),
};

// ============ CATALOGO DE GENETICA (panel del productor) ============
//
// La vidriera permanente. NO es un remate: no tiene fechas, esta prendida o
// apagada, y MUESTRA animales sin contenerlos (`muestra_en_catalogo` es una
// marca en `animal`, no un `id_evento`).
//
// UN CATALOGO AGRUPA N CABAÑAS. Pilaga tiene dos (Mercedes y Quimili) y quiere
// UNA sola web donde vivan las dos; otro cliente con dos cabañas puede querer
// dos sitios con marcas separadas. Cuantos catalogos puede tener lo decide su
// plan (`membresia.limite_catalogos`), y el limite cuenta URLS PUBLICAS, no
// cabañas: Manager puede agrupar sus dos cabañas en su unico sitio, lo que no
// puede es tener dos sitios.
//
// LA REGLA QUE SOSTIENE TODO: una cabaña vive en UN SOLO catalogo. Por eso la
// cadena animal → cabaña → catalogo es univoca y los flags del animal siguen
// siendo booleanos simples, sin necesidad de saber en que catalogo estan.
//
// OJO CON EL SCOPING. Los endpoints usan `solo_propios(Animal)` y NO
// `tenant_scoped`: un animal consignado por una cabaña invitada se puede
// GESTIONAR (ponerle precio, venderlo) pero NUNCA publicar como genetica
// propia. Por eso el listado para elegir animales se pide con
// `cliente: <id_clientes>` — ver `getCandidatos`.
export const catalogoGeneticaService = {
  // Todos los catalogos del tenant + el limite del plan + las cabañas.
  //
  // Cada cabaña viene con su `id_catalogo_genetica` (o null si esta libre), asi
  // que el front NO tiene que reimplementar la regla de una-cabaña-un-catalogo
  // para saber cual puede ofrecer.
  //   → { catalogos: [...], limite: {puede_crear, motivo, ...}, cabanas: [...] }
  getAll: () => api.get('/catalogo-genetica/'),

  // Body: { nombre_catalogo, cabanas: [ids] }. Nace APAGADO.
  //
  // El 409 trae el `motivo` ya redactado por el backend —plan agotado, sin
  // cabañas, o todas las cabañas ya publicadas—: se muestra tal cual en vez de
  // adivinarlo desde el front, que no sabe cual de los tres caso es.
  create: (data) => api.post('/catalogo-genetica/', data),

  get: (idCatalogo) => api.get(`/catalogo-genetica/${idCatalogo}`),
  update: (idCatalogo, data) => api.put(`/catalogo-genetica/${idCatalogo}`, data),
  remove: (idCatalogo) => api.delete(`/catalogo-genetica/${idCatalogo}`),

  // REEMPLAZA el conjunto de cabañas, no agrega: se manda la lista como quedo.
  // Igual que el orden de los animales, y por lo mismo — dos operaciones
  // (agregar/quitar) pueden dejar un estado intermedio si una falla.
  setCabanas: (idCatalogo, cabanas) =>
    api.put(`/catalogo-genetica/${idCatalogo}/cabanas`, { cabanas }),

  // Los animales YA marcados de ESE catalogo, en su orden.
  getAnimales: (idCatalogo) => api.get(`/catalogo-genetica/${idCatalogo}/animales`),

  // Los candidatos a marcar: el padron PROPIO, filtrable por raza, categoria,
  // busqueda (nombre + RP) y CABAÑA.
  //
  // ⚠️ DEPENDE DE OTRO RECURSO. La pantalla se gatea con `catalogo_genetica`,
  // pero esto pega a `GET /animales`, que pide `ver_animales`. Un plan con el
  // primero y sin el segundo renderiza una pantalla que funciona y una pestaña
  // "Animales disponibles" que tira 403. Hoy no muerde —Manager y Enterprise
  // llevan los dos— pero es la misma clase de dependencia cruzada que ya esta
  // anotada para `POST /animales/asignar-cabana` y `ver_cabanas`.
  //
  // `cliente: idCliente` NO es un filtro de conveniencia, es lo que alinea esta
  // lista con lo que el marcado acepta. `GET /animales` es `tenant_scoped` y
  // `Animal` es PERMEABLE_POR_INVITACION, asi que sin esto la pantalla ofrece
  // animales consignados que el backend despues rechaza por ajenos: el
  // productor selecciona 10, se aplican 7, y la UI no sabe cuales quedaron
  // afuera. Una pantalla no puede ofrecer lo que no puede cumplir.
  //
  // `idCliente` va como argumento propio y no dentro de `params` justamente
  // para que no se pueda olvidar: llamarlo sin el es un error visible aca y no
  // un lote a medio aplicar tres pantallas mas adelante.
  //
  // Para el admin de plataforma (rol 1 SIN id_clientes) `idCliente` es null y
  // se omite el filtro — que es lo correcto, porque `solo_propios` tambien lo
  // deja ver todo via `es_admin_plataforma()`.
  getCandidatos: (idCliente, params = {}, signal) =>
    // Delega en `animalService.getAll` en vez de pegarle a `/animales` por su
    // cuenta: ese endpoint tiene UN dueño en este archivo. Con dos, el dia que
    // cambien los nombres de los params o aparezca otra `vista`, este de aca
    // deja de seguir al otro y nadie se entera hasta que la lista sale mal.
    // Lo propio de este metodo es el `cliente` obligatorio, no el transporte.
    animalService.getAll(
      { ...params, ...(idCliente ? { cliente: idCliente } : {}) },
      signal,
    ),

  // Marca/desmarca en LOTE dentro de un catalogo. Los dos flags son opcionales.
  // El backend fuerza la jerarquia (apagar `muestra_en_catalogo` apaga
  // `apto_comercializar`) Y que el animal sea de una cabaña de ESTE catalogo.
  marcar: (idCatalogo, { animales, muestra_en_catalogo, apto_comercializar }) =>
    api.post(`/catalogo-genetica/${idCatalogo}/animales`, {
      animales,
      ...(muestra_en_catalogo !== undefined ? { muestra_en_catalogo } : {}),
      ...(apto_comercializar !== undefined ? { apto_comercializar } : {}),
    }),

  // Sube una imagen del catalogo y la deja apuntada en su config, en UNA sola
  // llamada. `campo` sale de `CAMPOS_IMAGEN`, en `catalogo_genetica_routes.py`:
  // 'hero_img', 'info_img', 'logo_img' o 'hero_overlay_img' — el backend tiene la lista
  // blanca, asi que mandar otra cosa es un 400 y no una ruta de archivo metida
  // adentro de un color.
  //
  // Guarda y persiste junto a proposito. Partirlo en dos pasos (subir a un
  // temporal y mandar la URL en el PUT de config) es exactamente lo que dejo
  // roto a GenerarPDF, que llama a un `POST /imagenes/upload-temp` que no
  // existe: el segundo paso se perdio y nadie se entero.
  subirImagen: (idCatalogo, file, campo = 'hero_img') => {
    const fd = new FormData();
    fd.append('imagen', file);
    fd.append('campo', campo);
    return api.post(`/catalogo-genetica/${idCatalogo}/imagen`, fd);
  },

  // El orden lo da la POSICION en la lista, no un numero por animal.
  ordenar: (idCatalogo, orden) =>
    api.put(`/catalogo-genetica/${idCatalogo}/animales/orden`, { orden }),
};

// ============ CONSULTAS DEL CATALOGO (panel del productor) ============
//
// La bandeja del lado privado de `catalogoPublicoService.consultar`: lo que el
// comprador manda desde la ficha publica cae aca, sin importar el
// `canal_consultas` que tenga el catalogo (whatsapp tambien registra).
export const consultaCatalogoService = {
  // `id_catalogo` y `estado` son opcionales — sin filtro trae todo el tenant.
  getAll: (params) => api.get('/consultas-catalogo/', { params }),
  setEstado: (id, estado) => api.put(`/consultas-catalogo/${id}`, { estado }),
};

export default api;
